/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { App, Image } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { NotificationLandingAction } from '@sportninja/common/features/notifications/types/landing'
import React from 'react'
import { useHistory } from 'react-router-dom'
import {
  font,
  heading1,
  heading6,
  paragraphLarge,
  weightBold,
  weightSemiBold,
} from '../../../components/css'
import { useNotificationDetail } from '../hooks/useNotificationDetail'
import NotificationDetailSkeleton from './NotificationDetailSkeleton'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

export interface NotificationDetailContentProps {
  notificationId: string
}

const NotificationDetailContent: React.FC<NotificationDetailContentProps> = ({
  notificationId,
}) => {
  'use memo'

  const history = useHistory()
  const { message } = App.useApp()
  const {
    isLoading,
    isError,
    errorMessage,
    title,
    body,
    messageText,
    logoUrl,
    primaryAction,
    secondaryAction,
    hasActions,
    busy,
    runAction,
  } = useNotificationDetail(notificationId)

  // There is no /notifications page — the list is a MainNav dropdown. Go back
  // to wherever the user came from, or Home for a deep link with no history.
  const goBack = () => {
    if (history.length > 1) {
      history.goBack()
    } else {
      history.push(ROUTES.HOME_ROOT)
    }
  }

  const handleAction = async (action: NotificationLandingAction) => {
    const { ok, errorMessage: actionError } = await runAction(action)
    if (ok) {
      message.success('Your response has been sent')
      goBack()
    } else {
      message.error(
        actionError ?? "We couldn't record your response. Please try again."
      )
    }
  }

  if (isLoading) {
    return <NotificationDetailSkeleton />
  }

  if (isError) {
    return (
      <div css={pageStyle}>
        <div css={cardStyle}>
          <p css={titleStyle}>{capitalizeWords('Notification Unavailable')}</p>
          <p css={bodyStyle}>
            {errorMessage ??
              'This notification may have been removed or is no longer available.'}
          </p>
          <div css={actionsStyle}>
            <button css={secondaryButtonStyle} onClick={goBack}>
              <span css={buttonLabelStyle}>Back</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div css={pageStyle}>
      <div css={cardStyle}>
        {!!logoUrl && (
          <Image
            width={80}
            height={80}
            css={logoStyle}
            src={logoUrl}
            fallback={TEAM_PLACEHOLDER}
            preview={false}
          />
        )}

        <p css={titleStyle}>{capitalizeWords(title || 'Notification')}</p>

        {!!body && <p css={bodyStyle}>{body}</p>}

        {!!messageText && (
          <div css={messageBoxStyle}>
            <span css={messageLabelStyle}>{capitalizeWords('Message:')}</span>
            <p css={messageTextStyle}>{messageText}</p>
          </div>
        )}

        <div css={dividerStyle} />

        <div css={actionsStyle}>
          {!!secondaryAction && (
            <button
              disabled={busy}
              css={secondaryButtonStyle}
              onClick={() => handleAction(secondaryAction)}
            >
              <span css={buttonLabelStyle}>{secondaryAction.label}</span>
            </button>
          )}
          {!!primaryAction && (
            <button
              disabled={busy}
              css={primaryButtonStyle}
              onClick={() => handleAction(primaryAction)}
            >
              <span css={buttonLabelStyle}>{primaryAction.label}</span>
            </button>
          )}
          {!hasActions && (
            <button css={secondaryButtonStyle} onClick={goBack}>
              <span css={buttonLabelStyle}>Close</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationDetailContent

// Centers the card in the viewport below the main nav ($main-nav-height: 56px
// in styles/_variables.scss). `min-height` rather than `height` so a tall card
// on a short viewport still scrolls instead of clipping.
const pageStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 56px);
  padding: 48px 16px;
`

// Card geometry copied verbatim from the invitation card in
// web/src/pages/InviteV2/index.jsx (~line 366) so both read as one treatment.
const cardStyle = css`
  display: flex;
  width: 600px;
  padding: 40px 24px;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${colors.SECONDARY_300};
  background: ${colors.SECONDARY_400};

  @media (max-width: 768px) {
    max-width: 100vw;
    min-width: 0;
    width: 100%;
  }
`

const logoStyle = css`
  border-radius: 8px;
`

// The invitation card hardcodes BarlowCondensed (SportNinja) / Neue Plak
// Condensed (Canlan). Those are exactly `font.heading` — NOT `font.title`,
// which is Rift on SportNinja. Use the token so Canlan stays correct.
const titleStyle = css`
  ${heading1}
  ${font.heading}
  color: ${colors.WHITE};
  text-align: center;
  text-transform: uppercase;
  margin: 0;
`

const bodyStyle = css`
  ${font.body}
  ${paragraphLarge}
  color: ${colors.WHITE};
  margin: 0;
  white-space: pre-line;
`

const messageBoxStyle = css`
  width: 100%;
  background-color: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`

const messageLabelStyle = css`
  ${font.body}
  ${weightBold}
  font-size: 16px;
  line-height: 22px;
  color: ${colors.WHITE};
`

const messageTextStyle = css`
  ${font.body}
  font-size: 14px;
  line-height: 20px;
  color: ${colors.WHITE};
  margin: 0;
  white-space: pre-line;
`

const dividerStyle = css`
  width: 100%;
  height: 1px;
  background-color: ${colors.SECONDARY_300};
`

const actionsStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  @media (max-width: 480px) {
    flex-direction: column;

    > * {
      width: 100%;
    }
  }
`

// Button treatment copied from the invitation card's NOT NOW / JOIN pair
// (web/src/pages/InviteV2/index.jsx ~line 816 and ~line 870).
const buttonBaseStyle = css`
  display: flex;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const secondaryButtonStyle = css`
  ${buttonBaseStyle}
  border: 2px solid ${colors.WHITE};
  background: transparent;
`

const primaryButtonStyle = css`
  ${buttonBaseStyle}
  border: 2px solid ${colors.PRIMARY};
  background: ${colors.PRIMARY};
`

// White on both buttons — the outlined secondary and the filled primary.
const buttonLabelStyle = css`
  ${heading6}
  ${font.heading}
  ${weightSemiBold}
  color: ${colors.WHITE};
  text-transform: uppercase;
`
